"use client";

import {
  createContext,
  createElement,
  useContext,
  type ReactNode,
} from "react";
import { useBrowserPublicHost } from "@/lib/use-browser-public-host";
import { useBrowserPublicLocationId } from "@/lib/use-browser-public-location-id";
import {
  buildSiteConfigForLocation,
  getLocationBySlug,
  isDefaultLocation,
  resolveLocationByHost,
  type PlatformLocation,
  type RestaurantSiteConfig,
} from "@/lib/restaurant-platform";
import { resolvePublicLocationCookie } from "@/lib/public-location-cookie";
import {
  buildRestaurantCopy,
  type MarketingEmailPopupCopy,
  type MarketingIncentivePopupCopy,
  type MarketingRestaurantCopy,
} from "@/lib/marketing-location-copy";

export type {
  MarketingEmailPopupCopy,
  MarketingIncentivePopupCopy,
  MarketingRestaurantCopy,
} from "@/lib/marketing-location-copy";

export type MarketingLocationContext = {
  location: PlatformLocation;
  siteConfig: RestaurantSiteConfig;
  restaurantCopy: MarketingRestaurantCopy;
  emailPopup: MarketingEmailPopupCopy;
  incentivePopup: MarketingIncentivePopupCopy;
  isDefaultLocation: boolean;
  romaFunnelEnabled: boolean;
};

type MarketingLocationHostContextValue = {
  initialPublicHost?: string;
  initialPublicLocationId?: string;
  romaFunnelEnabled?: boolean;
};

const MarketingLocationHostContext =
  createContext<MarketingLocationHostContextValue>({});

export function PublicMarketingLocationProvider({
  initialPublicHost,
  initialPublicLocationId,
  romaFunnelEnabled,
  children,
}: {
  initialPublicHost?: string;
  initialPublicLocationId?: string;
  romaFunnelEnabled?: boolean;
  children: ReactNode;
}) {
  return createElement(
    MarketingLocationHostContext.Provider,
    { value: { initialPublicHost, initialPublicLocationId, romaFunnelEnabled } },
    children,
  );
}

function isLocalDevPublicHost(host: string | undefined): boolean {
  if (!host) return false;

  try {
    const parsedHost = host.includes("://")
      ? new URL(host).hostname
      : host.split(":")[0];

    return (
      parsedHost === "localhost" ||
      parsedHost === "127.0.0.1" ||
      parsedHost === "::1"
    );
  } catch {
    return false;
  }
}

export function useMarketingLocationContext(): MarketingLocationContext {
  const { initialPublicHost, initialPublicLocationId, romaFunnelEnabled } = useContext(
    MarketingLocationHostContext,
  );
  const browserPublicHost = useBrowserPublicHost(initialPublicHost);
  const browserPublicLocationId = useBrowserPublicLocationId(
    initialPublicLocationId,
  );
  const cookieLocation = resolvePublicLocationCookie(browserPublicLocationId);
  const location =
    cookieLocation ??
    (romaFunnelEnabled && isLocalDevPublicHost(browserPublicHost)
      ? getLocationBySlug("roma-kitchen-southern-highlands")
      : resolveLocationByHost(browserPublicHost));
  const siteConfig = buildSiteConfigForLocation(location, {
    baseUrl: browserPublicHost,
  });
  const restaurantCopy = buildRestaurantCopy(location);

  return {
    location,
    siteConfig,
    restaurantCopy,
    emailPopup: restaurantCopy.emailPopup,
    incentivePopup: restaurantCopy.incentivePopup,
    isDefaultLocation: isDefaultLocation(location),
    romaFunnelEnabled: Boolean(romaFunnelEnabled),
  };
}
